import React from "react";
import { useEffect } from "react";

const About = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px"
    };

    const animateOnScroll = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;

          // Remove any existing animation classes
          element.classList.remove(
            'fadeInUp', 'fadeInLeft', 'fadeInRight',
            'zoomIn', 'slideInUp', 'animated'
          );

          // Add base animation class
          element.classList.add('animated');

          // Add specific animation based on data attribute or default
          const animationType = element.dataset.animation || 'fadeInUp';
          const delay = element.dataset.delay || '0';

          element.style.animationDelay = `${delay}ms`;
          element.classList.add(animationType);

          // Only animate once
          observer.unobserve(element);
        }
      });
    };

    const observer = new IntersectionObserver(animateOnScroll, observerOptions);

    // Observe all elements with animation classes
    document.querySelectorAll('.animated-section, [data-animation]').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>
        {`
        /* Base Animation Styles */
        .animated {
          animation-duration: 1s;
          animation-fill-mode: both;
          opacity: 0;
        }
        
        /* Animation Delays */
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-500 { animation-delay: 500ms; }
        
        /* Keyframe Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 40px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translate3d(-40px, 0, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translate3d(40px, 0, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale3d(0.8, 0.8, 0.8);
          }
          to {
            opacity: 1;
            transform: scale3d(1, 1, 1);
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translate3d(0, 100px, 0);
          }
          to {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }
        }
        
        /* Animation Classes */
        .fadeInUp {
          animation-name: fadeInUp;
        }
        
        .fadeInLeft {
          animation-name: fadeInLeft;
        }
        
        .fadeInRight {
          animation-name: fadeInRight;
        }
        
        .zoomIn {
          animation-name: zoomIn;
        }
        
        .slideInUp {
          animation-name: slideInUp;
        }
        
        /* Special animations for specific sections */
        .feature-item {
          transition: all 0.5s ease;
        }
        
        .feature-item:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.1);
        }
        `}
      </style>

      <section className="about-section animated-section" data-animation="fadeInUp">
        <div className="auto-container">
          <div className="row clearfix">
            <div className="col-lg-8 col-md-12 col-sm-12 big-column">
              <div className="inner-box animated-section px-3" data-animation="fadeInLeft">
                <div className="title-text animated-section" data-animation="fadeInUp">
                  <h2>ASYSTA — Advance & Systematic Training Academy</h2>
                </div>
                <div className="row clearfix">
                  <div className="col-lg-6 col-md-6 col-sm-12 image-column animated-section" data-animation="zoomIn">
                    <div className="image-box feature-item">
                      <figure className="image">
                        <img src={`${process.env.PUBLIC_URL}/assets/images/banner/trafic-lights.jpg`} alt="Traffic Lights - ASYSTA Driving Training" />
                      </figure>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 content-column animated-section" data-animation="fadeInRight">
                    <div className="content-box">
                      <div className="text animated-section" data-animation="fadeInUp" data-delay="100">
                        <p>Created with a simple belief: learning to drive should be structured, modern, and truly effective. Today's new‑generation learners expect more than just basic steering lessons. They want clarity, confidence, and a training experience that prepares them for real‑world driving, not just a license test and a support system that stays with them long after their training ends.</p>
                        <p>We built ASYSTA to bridge that gap and deliver exactly that.</p>
                      </div>
                     {/* <div
        className="btn-box animated-section"
        data-animation="fadeInUp"
        data-delay="200"
      >
        <Link to="/about" className="theme-btn btn-one">
          Read More
        </Link>
      </div> */}
                    </div>
                  </div>
                </div>
                <div className="row clearfix mt-4">
                  <div className="col-12">
                    <div className="content-box animated-section" data-animation="fadeInUp" data-delay="300">
                      <h4 className="mb-3 animated-section ct" data-animation="fadeInUp">Our Purpose</h4>
                      <div className="text animated-section" data-animation="fadeInUp" data-delay="400">
                        <p>Driving is a life skill, and mastering it requires more than knowing how to move a car. ASYSTA is committed to transforming the way India learns driving by introducing a phase‑by‑phase training approach that builds skills progressively and systematically.</p>
                        <p>Our goal is to help learners imbibe driving habits that match international standards, adapted thoughtfully for Indian roads, traffic patterns, and everyday challenges.</p>
                        <p>Our vision goes beyond driving lessons and we are creating a complete, end‑to‑end ecosystem for every new driver — from the moment they decide to learn driving to the day they confidently own and maintain their vehicle.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-12 col-sm-12 small-column animated-section" data-animation="fadeInRight" data-delay="200">
              <div className="about-service feature-item px-3">
                <div className="shape" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/images/shape/shape-11.png)` }}></div>
                <figure className="image animated-section" data-animation="zoomIn">
                  <img src={`${process.env.PUBLIC_URL}/assets/images/banner/student-driver.jpg`} alt="ASYSTA Student Driver Training" />
                </figure>
                <div className="text">
                  <h6 className="pb-2 animated-section ct" data-animation="fadeInUp">Here's what you can come to expect as a student of Asysta Driving School:</h6>
                  <ul className="list clearfix">
                    <li className="animated-section" data-animation="fadeInRight" data-delay="100">Proficient Training Experts</li>
                    <li className="animated-section" data-animation="fadeInRight" data-delay="200">Practical Learning through State-Of-The-Art Technology</li>
                    <li className="animated-section" data-animation="fadeInRight" data-delay="300">Educative Theoretical Sessions</li>
                    <li className="animated-section" data-animation="fadeInRight" data-delay="400">Systematic Phase-by-Phase Training</li>
                    <li className="animated-section" data-animation="fadeInRight" data-delay="500">International Standard Driving Habits</li>
                    <li className="animated-section" data-animation="fadeInRight" data-delay="600">End-to-End Driver Ecosystem Support</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

<section className="about-section what-we-stand-section">
  <div className="auto-container">
    <div className="row clearfix">
      <div className="col-lg-12 col-md-12 col-sm-12 big-column">
        <div className="px-3">
          <div className="title-text text-dark">
            <h2>What We Stand For</h2>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-6 col-md-6 col-sm-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-graph-up-arrow me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Advance Learning</h4>
                </div>
                <div className="text">
                  <p>We bring modern training methods, structured modules, and a learner‑first approach to ensure every student understands not just what to do, but why it matters.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-diagram-3 me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Systematic Training</h4>
                </div>
                <div className="text">
                  <p>Our phased curriculum breaks down driving into clear, manageable stages — from basic control to advanced road sense — helping learners build confidence step by step.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="feature-block">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-geo-alt me-3 text-primary fs-4" ></i>
                  <h4 className="feature-title mb-0 ct">Real‑World Readiness</h4>
                </div>
                <div className="text">
                  <p>Indian roads demand awareness, patience, and smart decision‑making. ASYSTA prepares learners for real traffic scenarios, not just textbook situations.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix mb-4">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="content-box">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle me-3 text-primary fs-4" ></i>
                  <h4 className="mb-0 ct">Driving Correctly, Not Just Driving</h4>
                </div>
                <div className="text">
                  <p>We believe that long‑term driving success comes from learning the right techniques from day one. Our training focuses on safe habits, proper control, lane discipline, and responsible driving behaviour that lasts a lifetime.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row clearfix">
            <div className="col-lg-12 shadow-sm p-3 rounded">
              <div className="content-box">
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-wrench-adjustable-circle me-3 text-primary fs-4" ></i>
                  <h4 className="mb-0 ct">A Complete Automotive Ecosystem</h4>
                </div>
                <div className="text">
                  <p>We support learners beyond training by offering:</p>
                  <ul className="list clearfix mt-2">
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-car-front me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Guidance on essential car accessories</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-tools me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Recommendations for nearby mechanics</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-droplet me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Car‑wash and detailing options</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-shield-check me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Access to trusted automotive services</span>
                    </li>
                    <li className="d-flex align-items-start mb-2">
                      <i className="bi bi-gear-wide-connected me-2" style={{ color: '#6c757d', marginTop: '0.2rem' }}></i>
                      <span>Local driving‑related resources and support</span>
                    </li>
                  </ul>
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</section>

<section
  className="about-section animated-section"
  data-animation="fadeInUp"
>
  <div className="auto-container">

    {/* TOP CENTER HEADING */}
    <div
      className="sec-title centered animated-section mb-5"
      data-animation="fadeInUp"
    >
      <h2>
        ASYSTA becomes the learner's go-to companion for everything they need
        on their driving journey
      </h2>

      <p className="mt-3">
        <strong>Your journey starts with ASYSTA</strong>
      </p>
    </div>

    <div className="row clearfix align-items-center">

      {/* LEFT SIDE – IMAGE */}
      <div
        className="col-lg-5 col-md-12 col-sm-12 image-column animated-section"
        data-animation="fadeInLeft"
      >
        <div className="image-box feature-item">
          <figure
            className="image animated-section"
            data-animation="zoomIn"
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/banner/course4.png`}
              alt="ASYSTA Vision and Mission"
            />
          </figure>
        </div>
      </div>

      {/* RIGHT SIDE – CONTENT */}
      <div
        className="col-lg-7 col-md-12 col-sm-12 content-column animated-section"
        data-animation="fadeInRight"
      >
        <div
          className="title-text animated-section mb-1"
          data-animation="fadeInUp"
        >
          <h2 className="ct">Our Mission</h2>
        </div>

        <div className="px-3">
          <div
            className="content-box animated-section"
            data-animation="fadeInUp"
            data-delay="300"
          >
            <div className="text">
              <p>
                Our mission is to empower new-age learners with a complete,
                end-to-end driving ecosystem that goes far beyond traditional
                training. ASYSTA delivers modern, structured, and internationally
                aligned driving education while becoming a one-stop solution for
                everything a new driver needs.
              </p>
              <p>
                From selecting the right driving course to buying essential
                accessories, and from finding trusted mechanics to locating nearby
                car-wash and automotive services — we support learners through every
                stage of their driving journey.
              </p>
              <p>
                We are committed to making driving education smarter, safer, and
                more convenient by integrating learning, guidance, and essential
                automotive services into one seamless platform.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* VISION SECTION */}
      <div className="col-12 mt-4">
        <div
          className="title-text animated-section mb-1"
          data-animation="fadeInUp"
        >
          <h2 className="ct">Our Vision</h2>
        </div>

        <div
          className="content-box animated-section"
          data-animation="fadeInUp"
          data-delay="100"
        >
          <div className="text">
            <p>
              To create a new generation of confident, responsible, and skilled
              drivers who understand the science of driving, respect the road,
              and realise the importance of a clean and well-maintained vehicle.
            </p>
            <p>
              We believe that responsible driving and proper vehicle care directly
              contribute to safer streets across India.
            </p>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>

    </>
  );
};

export default About;
